import { LightningIcon } from '@phosphor-icons/react';
import { CategoryIcon } from '@/components/CategoryIcon';
import { Badge } from '@/components/ui/badge';
import { expenseEntry, isAutomatic, memberColor, type SpaceExpenseRow } from '@/lib/spaces/expenses';
import { categoryChipStyleFromColor, resolveCategoryColor } from '@/lib/transactions/categories';
import { cn } from '@/lib/utils';
import type { SpaceDetail } from '@/types/spaces';
import { SpacePrivate } from './SpacePartnerPreview';

type Split = SpaceExpenseRow['splits'][number];

export function ExpenseCategoryIcon({ expense, icon, className }: Readonly<{ expense: Pick<SpaceExpenseRow, 'categoryId'>; icon: string | null | undefined; className: string }>) {
  const style = expense.categoryId ? categoryChipStyleFromColor(resolveCategoryColor(expense.categoryId)) : undefined;
  return <span aria-hidden className={cn('shrink-0 place-items-center rounded-xl bg-surface-sunken text-text-secondary', className)} style={style && { backgroundColor: style.backgroundColor, color: style.color }}>
    <CategoryIcon name={icon ?? 'receipt'} className="size-5" />
  </span>;
}

export function ExpenseEntry({ space, expense, userId }: Readonly<{ space: SpaceDetail; expense: SpaceExpenseRow; userId: string }>) {
  const entry = expenseEntry(space, expense, userId);
  if (!entry.privateValue) return <>{entry.label}</>;
  return <>{entry.label} · <SpacePrivate>{entry.privateValue}</SpacePrivate></>;
}

// `compact` (the home's short list): the automatic badge shrinks to its lightning, which keeps the name its room on a phone.
export function ExpensePills({ expense, compact = false }: Readonly<{ expense: Pick<SpaceExpenseRow, 'partial' | 'via'>; compact?: boolean }>) {
  return <><PartialPill visible={expense.partial} /><AutomaticPill visible={isAutomatic(expense)} compact={compact} /></>;
}
function PartialPill({ visible }: Readonly<{ visible: boolean }>) {
  if (!visible) return null;
  return <Badge variant="info" size="sm" className="shrink-0">parte</Badge>;
}
function AutomaticPill({ visible, compact }: Readonly<{ visible: boolean; compact: boolean }>) {
  if (!visible) return null;
  if (compact) return <LightningIcon role="img" aria-label="automática" weight="fill" className="size-3.5 shrink-0 text-primary" />;
  return <Badge variant="primary" size="sm" className="shrink-0"><LightningIcon aria-hidden weight="bold" className="size-3" />automática</Badge>;
}

export function ExpenseSplitBar({ space, splits }: Readonly<{ space: SpaceDetail; splits: Split[] }>) {
  const renderSegment = (split: Split) => <span key={split.userId} className={memberColor(space, split.userId)} style={{ flexGrow: split.amountCents }} />;
  return <div aria-hidden className="flex h-2 overflow-hidden rounded-full bg-surface-sunken">{splits.map(renderSegment)}</div>;
}
